import { useCallback, useEffect, useMemo, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  AlertCircle,
  ArrowUpRight,
  Check,
  Clock3,
  Crown,
  LogOut,
  MailCheck,
  PackageCheck,
  RefreshCw,
  SearchCheck,
  ShieldCheck,
  X,
} from "lucide-react";

import "@/components/admin/FoodReviewAdmin.css";

const supabaseUrl = String(process.env.REACT_APP_SUPABASE_URL || "").trim();
const supabaseKey = String(process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || "").trim();
const supabase = supabaseUrl && supabaseKey
  ? createClient(supabaseUrl, supabaseKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : null;

const FILTERS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "duplicate", label: "Duplicates" },
];

async function invokeAdmin(session, body) {
  if (!supabaseUrl || !supabaseKey || !session?.access_token) throw new Error("Admin configuration is unavailable.");
  const response = await fetch(`${supabaseUrl}/functions/v1/admin-food-review`, {
    method: "POST",
    headers: {
      apikey: supabaseKey,
      authorization: `Bearer ${session.access_token}`,
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error?.message || "The admin request failed.");
    error.code = payload?.error?.code;
    error.status = response.status;
    throw error;
  }
  return payload;
}

export function FoodReviewAdmin() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (!supabase) {
      setAuthReady(true);
      return undefined;
    }
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session || null);
        setAuthReady(true);
      }
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (active) {
        setSession(nextSession);
        setAuthReady(true);
        if (nextSession && window.location.href.includes("#")) {
          window.history.replaceState({}, document.title, "/admin/foods");
        }
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signIn = async (event) => {
    event.preventDefault();
    if (!supabase || authBusy) return;
    setAuthBusy(true);
    setAuthError("");
    const result = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/admin/foods`,
      },
    });
    if (result.error) {
      setAuthError("We couldn't send the sign-in link. Wait a moment and try again.");
    } else {
      setLinkSent(true);
    }
    setAuthBusy(false);
  };

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setSession(null);
  };

  if (!authReady) return <AdminLoading label="Checking admin session..." />;
  if (!supabase) return <ConfigurationMissing />;
  if (!session) return <AdminLogin email={email} linkSent={linkSent} busy={authBusy} error={authError} onEmail={setEmail} onReset={() => { setLinkSent(false); setAuthError(""); }} onSubmit={signIn} />;
  return <ReviewWorkspace session={session} onSignOut={signOut} />;
}

function AdminLogin({ email, linkSent, busy, error, onEmail, onReset, onSubmit }) {
  return (
    <main className="admin-auth-shell">
      <section className="admin-auth-card" aria-labelledby="admin-login-title">
        <div className="admin-mark" aria-hidden="true"><Crown size={24} /></div>
        <p className="admin-eyebrow">AKTUA OPERATIONS</p>
        <h1 id="admin-login-title">Food review</h1>
        <p className="admin-muted">Enter the authorized AKTUA email and we&apos;ll send a secure one-time sign-in link.</p>
        {error ? <div className="admin-alert" role="alert"><AlertCircle size={18} /><span>{error}</span></div> : null}
        {linkSent ? (
          <div className="admin-link-sent" role="status">
            <MailCheck size={26} />
            <div><strong>Check your email</strong><p>We sent a sign-in link to <span>{email.trim()}</span>. Open it on this device to continue.</p></div>
            <button type="button" className="admin-text-button" onClick={onReset}>Send another link</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="admin-form">
            <label htmlFor="admin-email">Email</label>
            <input id="admin-email" type="email" inputMode="email" autoCapitalize="none" autoComplete="email" required value={email} onChange={(event) => onEmail(event.target.value)} />
            <button className="admin-primary-button" type="submit" disabled={busy}>{busy ? "Sending link..." : "Send sign-in link"}</button>
          </form>
        )}
      </section>
    </main>
  );
}

function ReviewWorkspace({ session, onSignOut }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await invokeAdmin(session, { action: "list", status, limit: 100 });
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setSelectedId((current) => payload.items?.some((item) => item.candidate_id === current) ? current : payload.items?.[0]?.candidate_id || null);
    } catch (caught) {
      setError(caught.message || "The review queue could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [session, status]);

  useEffect(() => { void load(); }, [load]);
  const selected = useMemo(() => items.find((item) => item.candidate_id === selectedId) || null, [items, selectedId]);

  const reviewed = async (message) => {
    setNotice(message);
    await load();
  };

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <a className="admin-brand" href="/" aria-label="AKTUA home"><span className="admin-brand-mark"><Crown size={20} /></span><span>AKTUA</span><small>ADMIN</small></a>
        <div className="admin-account"><span>{session.user.email}</span><button type="button" onClick={onSignOut}><LogOut size={18} /><span>Sign out</span></button></div>
      </header>

      <main className="admin-main">
        <div className="admin-title-row">
          <div><p className="admin-eyebrow">COMMUNITY CATALOG</p><h1>Food review queue</h1><p className="admin-muted">Validate source, portions and nutrition before anything becomes searchable or scannable.</p></div>
          <button className="admin-secondary-button" type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={18} className={loading ? "admin-spin" : ""} />Refresh</button>
        </div>

        <nav className="admin-filters" aria-label="Review status">
          {FILTERS.map((filter) => <button key={filter.value} type="button" className={status === filter.value ? "is-active" : ""} aria-pressed={status === filter.value} onClick={() => setStatus(filter.value)}>{filter.label}</button>)}
        </nav>

        {notice ? <div className="admin-success" role="status"><Check size={18} /><span>{notice}</span><button type="button" aria-label="Dismiss notification" onClick={() => setNotice("")}><X size={17} /></button></div> : null}
        {error ? <div className="admin-alert" role="alert"><AlertCircle size={18} /><span>{error}</span></div> : null}

        {loading ? <AdminLoading label="Loading food submissions..." compact /> : items.length === 0 ? <EmptyQueue status={status} /> : (
          <div className="admin-workspace">
            <aside className="admin-queue" aria-label="Food submissions">
              {items.map((item) => <CandidateListItem key={item.candidate_id} item={item} selected={item.candidate_id === selectedId} onSelect={() => setSelectedId(item.candidate_id)} />)}
            </aside>
            <section className="admin-detail" aria-live="polite">
              {selected ? <CandidateDetail key={selected.candidate_id} item={selected} session={session} onReviewed={reviewed} /> : null}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function CandidateListItem({ item, selected, onSelect }) {
  return (
    <button type="button" className={`admin-candidate${selected ? " is-selected" : ""}`} onClick={onSelect} aria-pressed={selected}>
      <span className="admin-candidate-top"><strong>{item.food_name}</strong><StatusBadge status={item.status} /></span>
      <span>{item.brand || "No brand"} / {item.market_country}</span>
      <small><Clock3 size={13} />{new Date(item.created_at).toLocaleString()}</small>
    </button>
  );
}

function CandidateDetail({ item, session, onReviewed }) {
  const [notes, setNotes] = useState(item.review_notes || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const complete = item.core_macro_completeness === "complete" && [item.calories, item.protein_g, item.carbs_g, item.fat_g].every((value) => value !== null && value !== undefined);
  const canReview = item.status === "pending";

  const review = async (decision) => {
    if (!canReview || busy) return;
    if (decision === "rejected" && !notes.trim()) {
      setError("Add a rejection reason before saving.");
      return;
    }
    if (decision === "duplicate" && !item.possible_duplicate_food_id) {
      setError("No matching catalog food is attached to this submission.");
      return;
    }
    const verb = decision === "approved" ? "Approve and publish" : decision === "duplicate" ? "Mark as duplicate" : "Reject";
    if (!window.confirm(`${verb} "${item.food_name}"?`)) return;
    setBusy(true);
    setError("");
    try {
      await invokeAdmin(session, {
        action: "review",
        candidateId: item.candidate_id,
        decision,
        reviewNotes: notes.trim() || null,
        duplicateFoodId: decision === "duplicate" ? item.possible_duplicate_food_id : null,
      });
      await onReviewed(decision === "approved" ? "Food approved and added to the community catalog." : decision === "duplicate" ? "Submission marked as a duplicate." : "Submission rejected.");
    } catch (caught) {
      setError(caught.message || "The review could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <article>
      <div className="admin-detail-heading">
        <div><StatusBadge status={item.status} /><h2>{item.food_name}</h2><p>{item.brand || "No brand supplied"}</p></div>
        <div className={`admin-validation ${complete ? "is-valid" : "is-warning"}`}>{complete ? <ShieldCheck size={19} /> : <AlertCircle size={19} />}<span>{complete ? "Core macros complete" : "Nutrition incomplete"}</span></div>
      </div>

      <section className="admin-section"><h3>Nutrition snapshot</h3><div className="admin-macro-grid"><Metric label="Calories" value={formatNumber(item.calories, "kcal")} /><Metric label="Protein" value={formatNumber(item.protein_g, "g")} /><Metric label="Carbs" value={formatNumber(item.carbs_g, "g")} /><Metric label="Fat" value={formatNumber(item.fat_g, "g")} /><Metric label="Fiber" value={formatNumber(item.fiber_g, "g")} /></div></section>

      <section className="admin-section"><h3>Serving and identity</h3><dl className="admin-facts"><Fact label="Serving" value={`${item.serving_amount} ${item.serving_unit}${item.serving_grams ? ` / ${item.serving_grams} g` : ""}`} /><Fact label="Nutrition basis" value={item.nutrition_basis === "per_100g" ? "Per 100 g" : "Per serving"} /><Fact label="Barcode" value={item.barcode || "Not supplied"} /><Fact label="Market" value={item.market_country} /><Fact label="Source type" value={humanize(item.source_type)} /><Fact label="Submitted" value={new Date(item.created_at).toLocaleString()} /></dl></section>

      <section className="admin-section"><h3>Evidence</h3>{item.source_url ? <a className="admin-source-link" href={item.source_url} target="_blank" rel="noreferrer">Open submitted source <ArrowUpRight size={16} /></a> : <p className="admin-muted">No source URL supplied.</p>}{item.submitter_notes ? <blockquote>{item.submitter_notes}</blockquote> : null}</section>

      {item.possible_duplicate_food_id ? <section className="admin-duplicate"><SearchCheck size={21} /><div><strong>Possible duplicate</strong><p>{item.possible_duplicate_name || item.possible_duplicate_food_id}{item.possible_duplicate_brand ? ` / ${item.possible_duplicate_brand}` : ""}{item.possible_duplicate_barcode ? ` / ${item.possible_duplicate_barcode}` : ""}</p></div></section> : null}

      <section className="admin-section"><label htmlFor={`review-notes-${item.candidate_id}`}>Review notes</label><textarea id={`review-notes-${item.candidate_id}`} value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={2000} disabled={!canReview || busy} placeholder="Record what you checked or why this should not be published." /><small className="admin-character-count">{notes.length}/2000</small>{error ? <div className="admin-alert" role="alert"><AlertCircle size={18} /><span>{error}</span></div> : null}</section>

      {canReview ? <div className="admin-actions"><button type="button" className="admin-approve" disabled={!complete || busy} onClick={() => void review("approved")}><PackageCheck size={18} />{busy ? "Saving..." : "Approve & publish"}</button><button type="button" className="admin-duplicate-button" disabled={!item.possible_duplicate_food_id || busy} onClick={() => void review("duplicate")}><SearchCheck size={18} />Duplicate</button><button type="button" className="admin-reject" disabled={busy} onClick={() => void review("rejected")}><X size={18} />Reject</button></div> : <p className="admin-reviewed-note"><ShieldCheck size={18} />Reviewed {item.reviewed_at ? new Date(item.reviewed_at).toLocaleString() : ""}</p>}
    </article>
  );
}

function Metric({ label, value }) { return <div className="admin-metric"><span>{label}</span><strong>{value}</strong></div>; }
function Fact({ label, value }) { return <div><dt>{label}</dt><dd>{value}</dd></div>; }
function StatusBadge({ status }) { return <span className={`admin-status admin-status-${status}`}>{status}</span>; }
function formatNumber(value, suffix) { return value == null ? "--" : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${suffix}`; }
function humanize(value) { return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()); }

function EmptyQueue({ status }) { return <section className="admin-empty"><ShieldCheck size={34} /><h2>Nothing to review</h2><p>No {status} food submissions are in the queue.</p></section>; }
function AdminLoading({ label, compact = false }) { return <div className={`admin-loading${compact ? " is-compact" : ""}`} role="status"><RefreshCw className="admin-spin" size={24} /><span>{label}</span></div>; }
function ConfigurationMissing() { return <main className="admin-auth-shell"><section className="admin-auth-card"><div className="admin-mark"><AlertCircle size={24} /></div><p className="admin-eyebrow">AKTUA OPERATIONS</p><h1>Configuration needed</h1><p className="admin-muted">Set the website&apos;s public Supabase URL and publishable key before building this portal.</p></section></main>; }
